import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import ExhibitionBoothLeadApi from "../../../../../infrastructure/api/exhibitionBoothLead";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";

const ROLE_OPTIONS = [
  { value: "", label: "همه" },
  { value: 0, label: "متقاضی" },
  { value: 1, label: "پیمانکار" },
  { value: 2, label: "تامین‌کننده" },
];

const normalizeRow = (raw) => ({
  ...raw,
  id: raw?.id ?? raw?.Id,
  mobile: raw?.mobile ?? raw?.Mobile ?? "",
  fullName: raw?.fullName ?? raw?.FullName ?? "",
  roleTitle: raw?.roleTitle ?? raw?.RoleTitle ?? "",
  source: raw?.source ?? raw?.Source ?? "",
  createOn: raw?.createOn ?? raw?.CreateOn ?? null,
  smsSentAt: raw?.smsSentAt ?? raw?.SmsSentAt ?? null,
  smsStatus: raw?.smsStatus ?? raw?.SmsStatus ?? "",
});

const ExhibitionBoothLeadsPage = ({ api = ExhibitionBoothLeadApi }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [filterTick, setFilterTick] = useState(0);
  const [filters, setFilters] = useState({
    role: "",
    search: "",
    fromDate: null,
    toDate: null,
  });
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const loadReport = useCallback(async () => {
    try {
      const res = await api.getReport();
      if (res?.isSuccess) setReport(res.data);
    } catch {
      setReport(null);
    }
  }, [api]);

  const loadList = useCallback(async () => {
    const f = filtersRef.current;
    const params = { page: page + 1, pageSize };
    if (f.role !== "") params.role = Number(f.role);
    if (f.search) params.search = f.search;
    if (f.fromDate) params.fromDate = f.fromDate;
    if (f.toDate) params.toDate = f.toDate;

    try {
      setLoading(true);
      const res = await api.getPagedList(params);
      if (res?.isSuccess) {
        setRows((res.data?.items || res.data?.Items || []).map(normalizeRow));
        setTotal(res.data?.total ?? res.data?.Total ?? 0);
      } else {
        toast.error(res?.message || "خطا در دریافت لیست");
      }
    } catch {
      toast.error("خطا در ارتباط با سرور");
    } finally {
      setLoading(false);
    }
  }, [api, page, pageSize]);

  useEffect(() => {
    loadReport();
  }, [loadReport, filterTick]);

  useEffect(() => {
    loadList();
  }, [loadList, filterTick, page, pageSize]);

  const columns = useMemo(
    () => [
      { id: "mobile", label: "موبایل", minWidth: 120 },
      { id: "fullName", label: "نام", minWidth: 140 },
      { id: "roleTitle", label: "نوع مخاطب", minWidth: 100 },
      { id: "source", label: "منبع", minWidth: 100 },
      {
        id: "createOn",
        label: "تاریخ ثبت",
        minWidth: 130,
        format: (v) => (v ? new Date(v).toLocaleString("fa-IR") : "—"),
      },
      {
        id: "smsSentAt",
        label: "زمان پیامک",
        minWidth: 130,
        format: (v) => (v ? new Date(v).toLocaleString("fa-IR") : "—"),
      },
      { id: "smsStatus", label: "وضعیت پیامک", minWidth: 90 },
    ],
    []
  );

  const onExport = async () => {
    const f = filtersRef.current;
    const params = {};
    if (f.role !== "") params.role = Number(f.role);
    if (f.search) params.search = f.search;
    if (f.fromDate) params.fromDate = f.fromDate;
    if (f.toDate) params.toDate = f.toDate;

    try {
      const res = await api.exportCsv(params);
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `exhibition-booth-leads-${Date.now()}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("خطا در دریافت فایل");
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <BreadcrumbsComponent />
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        لیدهای غرفه نمایشگاه
      </Typography>

      {report ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: "کل ثبت‌ها", value: report.total ?? report.Total },
            { label: "متقاضی", value: report.applicantCount ?? report.ApplicantCount },
            { label: "پیمانکار", value: report.contractorCount ?? report.ContractorCount },
            { label: "تامین‌کننده", value: report.supplierCount ?? report.SupplierCount },
            { label: "پیامک ارسال‌شده", value: report.smsSentCount ?? report.SmsSentCount },
          ].map((item) => (
            <Grid item xs={6} md={2.4} key={item.label}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {item.value ?? 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="جستجو (موبایل / نام)"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>نوع مخاطب</InputLabel>
              <Select
                label="نوع مخاطب"
                value={filters.role}
                onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <MenuItem key={String(opt.value)} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <JalaliDatePicker
              label="از تاریخ"
              value={filters.fromDate}
              onChange={(v) => setFilters((f) => ({ ...f, fromDate: v }))}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <JalaliDatePicker
              label="تا تاریخ"
              value={filters.toDate}
              onChange={(v) => setFilters((f) => ({ ...f, toDate: v }))}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => {
                  setPage(0);
                  setFilterTick((x) => x + 1);
                }}
              >
                اعمال فیلتر
              </Button>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => setFilterTick((x) => x + 1)}>
                بروزرسانی
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onExport}>
                CSV
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <EnhancedTable
        columns={columns}
        rows={rows}
        total={total}
        page={page}
        pageSize={pageSize}
        loading={loading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </Box>
  );
};

export default ExhibitionBoothLeadsPage;
